import React, { useCallback, useEffect, useState } from 'react';
import AdminLayout from '../components/AdminLayout';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import './AdminSecurity.css';

// Optional two-step sign-in (authenticator app) for the signed-in staff member. Recommended for super-admins.
const AdminSecurity = () => {
  const { user } = useAuth();
  const [factor, setFactor] = useState(null); // verified TOTP factor, if any
  const [enrollment, setEnrollment] = useState(null); // { id, qr, secret } while enrolling
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const refresh = useCallback(async () => {
    const { data, error } = await supabase.auth.mfa.listFactors();
    if (error) {
      setMessage({ type: 'error', text: 'Could not load your security settings' });
    } else {
      setFactor(data.totp?.[0] ?? null);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const startEnrollment = async () => {
    setBusy(true);
    setMessage({ type: '', text: '' });
    try {
      // Clear abandoned (unverified) enrolments first; Supabase rejects duplicates by name.
      const { data: all } = await supabase.auth.mfa.listFactors();
      for (const stale of all?.all?.filter((f) => f.factor_type === 'totp' && f.status === 'unverified') ?? []) {
        await supabase.auth.mfa.unenroll({ factorId: stale.id });
      }
      const { data, error } = await supabase.auth.mfa.enroll({ factorType: 'totp', friendlyName: 'Authenticator app' });
      if (error) throw error;
      setEnrollment({ id: data.id, qr: data.totp.qr_code, secret: data.totp.secret });
    } catch (error) {
      setMessage({ type: 'error', text: 'Could not start setup. Please try again.' });
    } finally {
      setBusy(false);
    }
  };

  const confirmEnrollment = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMessage({ type: '', text: '' });
    const { error } = await supabase.auth.mfa.challengeAndVerify({ factorId: enrollment.id, code: code.trim() });
    if (error) {
      setMessage({ type: 'error', text: 'That code is not valid. Check your authenticator app and try again.' });
    } else {
      setEnrollment(null);
      setCode('');
      setMessage({ type: 'success', text: 'Two-step sign-in is now on. You will be asked for a code each time you sign in.' });
      await refresh();
    }
    setBusy(false);
  };

  const cancelEnrollment = async () => {
    await supabase.auth.mfa.unenroll({ factorId: enrollment.id });
    setEnrollment(null);
    setCode('');
  };

  const turnOff = async () => {
    if (!window.confirm('Turn off two-step sign-in for your account?')) return;
    setBusy(true);
    const { error } = await supabase.auth.mfa.unenroll({ factorId: factor.id });
    setMessage(error
      ? { type: 'error', text: 'Could not turn off two-step sign-in. Please try again.' }
      : { type: 'success', text: 'Two-step sign-in is off.' });
    await refresh();
    setBusy(false);
  };

  return (
    <AdminLayout>
      <div className="admin-security">
        <h1>Security</h1>
        <p className="subtitle">Protect your {user?.role} account with a code from an authenticator app.</p>

        {message.text && <div className={`alert alert-${message.type}`} role="status">{message.text}</div>}

        {loading ? (
          <div className="loading">Loading...</div>
        ) : enrollment ? (
          <form onSubmit={confirmEnrollment} className="security-card">
            <h2>Set up an authenticator app</h2>
            <ol>
              <li>Open an authenticator app (Google Authenticator, Microsoft Authenticator, Authy or similar).</li>
              <li>Scan this QR code, or enter the key by hand.</li>
              <li>Type the 6-digit code the app shows.</li>
            </ol>
            <img src={enrollment.qr} alt="QR code for your authenticator app" className="security-qr" />
            <p>Key: <code>{enrollment.secret}</code></p>
            <label htmlFor="enroll-code">6-digit code</label>
            <input
              id="enroll-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              required
            />
            <div className="security-actions">
              <button type="submit" className="btn-save" disabled={busy || code.length !== 6}>Turn on</button>
              <button type="button" onClick={cancelEnrollment} disabled={busy}>Cancel</button>
            </div>
          </form>
        ) : factor ? (
          <div className="security-card">
            <h2>Two-step sign-in is on</h2>
            <p>Each sign-in needs your password and a code from your authenticator app.</p>
            <button type="button" onClick={turnOff} disabled={busy}>Turn off</button>
          </div>
        ) : (
          <div className="security-card">
            <h2>Two-step sign-in is off</h2>
            <p>
              Adding a code from an authenticator app stops anyone who learns your password from signing in.
              {user?.role === 'super-admin' && ' It is strongly recommended for super-admin accounts.'}
            </p>
            <button type="button" className="btn-save" onClick={startEnrollment} disabled={busy}>Set up</button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminSecurity;
